import { useMemo } from 'react';
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  FileTextIcon,
  XIcon,
} from 'lucide-react';

import type { WorkflowView } from '@/lib/api/generated/model';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard';

import { promptReadingOrder } from '../graph/workflowToGraph';
import { useWorkflows } from '../hooks/useWorkflows';
import { StepProfile } from './StepProfile';
import { GATE_MODE_LABEL, STEP_KIND_LABEL } from './stepLabels';

/**
 * A step's whole prompt, read from the diagram beside its board (spec 281).
 *
 * Every step card says "Open full prompt…", and on the workflow page and in the
 * builder a click does open it. The board's diagram was the one canvas where it
 * did nothing, and it is where people read a workflow while watching its tasks.
 *
 * A dialog rather than the workflow page's side panel: the diagram beside a
 * board is a short box, and a long prompt read in a 384px column inside it is
 * the cramped view the report was about. The prompt is shown verbatim — it is
 * the text the agent receives, so markdown is not rendered and the author's
 * indents and blank lines are kept (FR-004).
 *
 * Previous and next walk the declared steps in the diagram's own order, so a
 * whole workflow can be read without closing this (FR-006).
 */
export function StepPromptDialog({
  workspaceId,
  workflow,
  stepId,
  open,
  onStepChange,
  onClose,
}: {
  workspaceId: string;
  workflow: WorkflowView;
  /**
   * The step shown. Kept after closing, so the dialog still has something to
   * draw while it animates out; an id no longer in the workflow closes it.
   */
  stepId: string | null;
  open: boolean;
  onStepChange: (stepId: string) => void;
  onClose: () => void;
}) {
  const order = useMemo(() => promptReadingOrder(workflow), [workflow]);
  const index = order.findIndex((entry) => entry.step.id === stepId);
  const current = index === -1 ? undefined : order[index];
  const previous = index > 0 ? order[index - 1] : undefined;
  const next = index !== -1 ? order[index + 1] : undefined;

  return (
    <Dialog
      open={open && current !== undefined}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {current && (
        <DialogContent
          className="sm:max-w-2xl"
          // Back to the card of the step being read, which after Previous or
          // Next is not the card that opened this (FR-009).
          finalFocus={() =>
            document.querySelector<HTMLElement>(
              `.react-flow__node[data-id="${CSS.escape(current.step.id)}"]`,
            )
          }
        >
          <StepPromptBody
            workspaceId={workspaceId}
            step={current.step}
            phaseName={current.phaseName}
          />

          <DialogFooter className="sm:justify-between">
            {(previous || next) && (
              <div className="flex gap-2">
                {previous && (
                  <Button
                    variant="outline"
                    aria-label={`Previous step: ${previous.step.name}`}
                    onClick={() => onStepChange(previous.step.id)}
                    className="max-w-60 max-md:max-w-none max-md:flex-1"
                  >
                    <ChevronLeftIcon aria-hidden="true" />
                    <span className="truncate">{previous.step.name}</span>
                  </Button>
                )}
                {next && (
                  <Button
                    variant="outline"
                    aria-label={`Next step: ${next.step.name}`}
                    onClick={() => onStepChange(next.step.id)}
                    className="max-w-60 max-md:max-w-none max-md:flex-1"
                  >
                    <span className="truncate">{next.step.name}</span>
                    <ChevronRightIcon aria-hidden="true" />
                  </Button>
                )}
              </div>
            )}
            <div className="flex gap-2">
              {current.step.prompt ? (
                <CopyPromptButton
                  // Keyed by step, so "Copied" never carries over to the next prompt.
                  key={current.step.id}
                  prompt={current.step.prompt}
                />
              ) : null}
              <DialogClose
                render={<Button variant="outline" className="max-md:flex-1" />}
              >
                Close
              </DialogClose>
            </div>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
}

function StepPromptBody({
  workspaceId,
  step,
  phaseName,
}: {
  workspaceId: string;
  step: ReturnType<typeof promptReadingOrder>[number]['step'];
  phaseName: string;
}) {
  const kind =
    step.type === 'human_gate'
      ? 'human_gate'
      : step.type === 'linked_workflow'
        ? 'linked_workflow'
        : 'ai_step';
  const facts = [STEP_KIND_LABEL[kind], phaseName];
  if (kind === 'human_gate' && step.mode != null) {
    facts.push(GATE_MODE_LABEL[step.mode] ?? step.mode);
  }
  // A gate a person always resolves runs on no profile, whatever it names.
  const runsOnProfile =
    step.llm != null && !(kind === 'human_gate' && step.mode !== 'ai_or_human');

  return (
    <>
      <DialogHeader>
        <DialogTitle className="pe-8">{step.name}</DialogTitle>
        <DialogDescription>{facts.join(' · ')}</DialogDescription>
      </DialogHeader>

      {kind === 'linked_workflow' && (
        <LinkedWorkflowLine workspaceId={workspaceId} target={step.workflow} />
      )}
      {runsOnProfile && step.llm != null && <StepProfile slug={step.llm} />}

      <div className="flex min-w-0 flex-col gap-1">
        <EyebrowLabel icon={FileTextIcon}>Prompt</EyebrowLabel>
        {step.prompt ? (
          <pre
            data-testid="step-prompt"
            // `tabIndex` so a keyboard user can scroll a long prompt. On a phone
            // it takes its full height and the sheet scrolls instead: one scroll
            // inside another cut the prompt off under the sheet's footer.
            tabIndex={0}
            aria-label={`Prompt of ${step.name}`}
            className="bg-muted/40 border-border text-foreground focus-visible:ring-ring max-h-[60vh] overflow-auto max-md:max-h-none rounded-md border p-3 font-mono text-sm leading-relaxed whitespace-pre-wrap focus-visible:ring-3 focus-visible:outline-none"
          >
            {step.prompt}
          </pre>
        ) : (
          <p className="text-muted-foreground text-sm">
            This step has no prompt.
          </p>
        )}
      </div>
    </>
  );
}

function LinkedWorkflowLine({
  workspaceId,
  target,
}: {
  workspaceId: string;
  target: string | null | undefined;
}) {
  const { workflows } = useWorkflows(workspaceId);
  const name = target
    ? workflows?.find((workflow) => workflow.key === target)?.name
    : undefined;
  return (
    <p className="text-foreground text-sm">
      {name
        ? `Runs the workflow ${name}.`
        : 'Runs a workflow that cannot be found in this workspace.'}
    </p>
  );
}

function CopyPromptButton({ prompt }: { prompt: string }) {
  const { copy, state } = useCopyToClipboard();
  const label =
    state === 'copied'
      ? 'Copied'
      : state === 'failed'
        ? 'Copy failed'
        : 'Copy prompt';
  return (
    <Button
      variant="outline"
      className="max-md:flex-1"
      onClick={() => {
        void copy(prompt);
      }}
    >
      {state === 'copied' ? (
        <CheckIcon aria-hidden="true" />
      ) : state === 'failed' ? (
        <XIcon aria-hidden="true" />
      ) : (
        <CopyIcon aria-hidden="true" />
      )}
      {label}
    </Button>
  );
}
